import { AnnotatedStringEdit, DiffAnnotation } from './annotatedStringEdit.js';
import { Move } from './move.js';

/**
 * Output of a diff computation.
 */
export class DiffResult {
	constructor(
		/**
		 * Parallel set of replacements transforming `original` into `modified`,
		 * excluding any replacements that are part of a `Move`.
		 *
		 * When no moves are computed: `edits.apply(original) === modified`.
		 */
		readonly edits: AnnotatedStringEdit<DiffAnnotation>,

		/**
		 * Detected text moves. Empty unless `DiffOptions.computeMoves` is set.
		 */
		readonly moves: readonly Move[],

		/**
		 * `true` if the algorithm aborted because of `maxComputationTimeMs`.
		 * The result is still valid but may be coarser than ideal.
		 */
		readonly hitTimeout: boolean,
	) { }
}

export interface DiffOptions {
	/**
	 * Algorithm hint. Pure-TS computer auto-selects based on input size if omitted.
	 * The WASM computer currently ignores this hint.
	 */
	algorithm?: 'myers' | 'dynamic-programming';

	/** Maximum computation time in milliseconds. 0 (default) means no timeout. */
	maxComputationTimeMs?: number;

	/** Ignore leading and trailing whitespace differences. Default: false. */
	ignoreTrimWhitespace?: boolean;

	/** Detect moved blocks of text. Default: false. */
	computeMoves?: boolean;

	/** Extend diffs to subword boundaries. Default: false. */
	extendToSubwords?: boolean;
}

export interface IDiffComputer {
	computeDiff(original: string, modified: string, options?: DiffOptions): DiffResult;
}
